import { NovatiqAuthorizationClient } from '@/_auth-client';
import { APPLICATION_MODE } from '@/_constants';
import { AuthorizationHelper, NovatiqPopupHelper, ToastorHelper } from '@/_helpers';
import { ILanguage, LanguageService } from '@/_services/language.service';
import { AppStorage } from '@/_storage';
import { NovatiqError } from '@/_types';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  model,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { Toast } from 'primeng/toast';
import { catchError, filter, throwError } from 'rxjs';
import { AppSidebarComponent } from './app.sidebar.component';
import { AppTopBarComponent } from './app.topbar.component';
import { LayoutService } from './service/app.layout.service';
import { NovatiqMessageService, DEFAULT_TOAST_LIFE_MS } from './service/message.service';

@Component({
  selector: 'app-layout',
  templateUrl: './app.layout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    ReactiveFormsModule,
    TranslateModule,
    Button,
    ConfirmDialog,
    Toast,
    Dialog,
    Select,
    AppTopBarComponent,
    AppSidebarComponent
  ]
})
export class AppLayoutComponent implements OnInit, OnDestroy {
  private static readonly LOGOUT_CONFIRM_KEY = 'logout';

  readonly layoutService = inject(LayoutService);
  private readonly renderer = inject(Renderer2);
  private readonly router = inject(Router);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly authorizationHelper = inject(AuthorizationHelper);
  private readonly authorizationClient = inject(NovatiqAuthorizationClient);
  private readonly dataStorage = inject(AppStorage);
  private readonly popupHelper = inject(NovatiqPopupHelper);
  private readonly toaster = inject(ToastorHelper);
  private readonly translate = inject(TranslateService);
  private readonly novatiqMessageService = inject(NovatiqMessageService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly languageService = inject(LanguageService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(AppSidebarComponent) appSidebar!: AppSidebarComponent;
  @ViewChild(AppTopBarComponent) appTopbar!: AppTopBarComponent;

  private menuOutsideClickListener: (() => void) | null = null;
  private profileMenuOutsideClickListener: (() => void) | null = null;

  readonly dialogDefaultLanguage = model(false);
  readonly languages = signal<ILanguage[]>([]);

  readonly languageForm = this.fb.group({
    defaultLanguage: [this.authorizationClient.locale, Validators.required]
  });

  readonly layoutConfig = computed(() => this.layoutService.config());
  readonly layoutState = computed(() => this.layoutService.state());

  readonly collapsibleIcon = computed(() => {
    const config = this.layoutConfig();
    const state = this.layoutState();
    if (config.menuMode !== 'static') {
      return undefined;
    }
    return state.staticMenuDesktopInactive ? 'pi pi-angle-right collapsible-icon' : 'pi pi-angle-left collapsible-icon';
  });

  constructor() {
    this.layoutService.overlayOpen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.setupOutsideClickListeners();
      if (this.layoutService.state().staticMenuMobileActive) {
        this.blockBodyScroll();
      }
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.hideMenu();
        this.hideProfileMenu();
        this.changeDetectorRef.markForCheck();
      });

    this.novatiqMessageService
      .getMessage()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((message) => {
        if (message) {
          this.messageService.add({
            severity: message.severity,
            detail: message.text,
            key: message.key,
            life: message.life ?? DEFAULT_TOAST_LIFE_MS
          });
        } else {
          this.messageService.clear();
        }
      });
  }

  ngOnInit(): void {
    this.checkForShowDefaultLangDialog();
  }

  menuClicked(action: string): void {
    if (action === 'logout') {
      this.getConfirmation();
    }
  }

  hideMenu(): void {
    this.layoutService.resetMenuState();
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }
    this.unblockBodyScroll();
  }

  hideProfileMenu(): void {
    this.layoutService.hideProfileSidebar();
    if (this.profileMenuOutsideClickListener) {
      this.profileMenuOutsideClickListener();
      this.profileMenuOutsideClickListener = null;
    }
  }

  blockBodyScroll(): void {
    document.body.classList.add('blocked-scroll');
  }

  unblockBodyScroll(): void {
    document.body.classList.remove('blocked-scroll');
  }

  getConfirmation(): void {
    this.dataStorage.RemoveItem(APPLICATION_MODE.EDIT_MODE);
    if (this.dataStorage.ContainsKey(APPLICATION_MODE.EDIT_MODE)) {
      this.popupHelper.open('unsavedChanges');
    } else {
      this.confirmationService.confirm({
        key: AppLayoutComponent.LOGOUT_CONFIRM_KEY,
        message: this.translate.instant('LogoutMessage'),
        accept: () => {
          this.logOut();
        }
      });
    }
  }

  logOut(): void {
    this.popupHelper.close();
    this.authorizationHelper
      .logOut()
      .pipe(
        catchError((error: NovatiqError) => {
          this.toaster.errorMessage(error.message);
          return throwError(() => error);
        })
      )
      .subscribe();
  }

  closeDefaultLanguageDialog(): void {
    this.dialogDefaultLanguage.set(false);
  }

  checkForShowDefaultLangDialog(): void {
    this.languageService.languages$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.languages.set(data);
        const { loggedInUser, locale } = this.authorizationClient;
        const isDefaultLanguageSelected = Boolean(locale);
        const orgPreferredLang = loggedInUser?.organisation?.preferredLanguage || '';
        const isOrgPreferredLangAnActiveFusionLang = data.some((lang) => lang.iso === orgPreferredLang);
        if (isOrgPreferredLangAnActiveFusionLang && !isDefaultLanguageSelected && orgPreferredLang !== 'en') {
          this.dialogDefaultLanguage.set(true);
        }
      },
      error: (error) => {
        this.showErrorMessage(error?.message);
      }
    });
  }

  saveLanguage(): void {
    const selectedLanguage = this.languageForm.value.defaultLanguage;
    if (!selectedLanguage) {
      return;
    }

    this.languageService
      .saveLanguage(selectedLanguage)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.authorizationClient.updateDefaultLanguage(selectedLanguage);
          this.languageService.setSessionLanguage(selectedLanguage);
          this.languageService.useLanguage(selectedLanguage);
          this.dialogDefaultLanguage.set(false);
          setTimeout(() => {
            this.novatiqMessageService.sendMessage(this.translate.instant('userProfileUpdatedSuccess'), 'success');
          }, 500);
        },
        error: (error) => {
          this.showErrorMessage(error?.message);
        }
      });
  }

  showErrorMessage(message: string | undefined): void {
    this.novatiqMessageService.sendMessage(message || '', 'error');
  }

  ngOnDestroy(): void {
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }
    if (this.profileMenuOutsideClickListener) {
      this.profileMenuOutsideClickListener();
    }
  }

  private setupOutsideClickListeners(): void {
    if (!this.menuOutsideClickListener) {
      this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
        const target = event.target as HTMLElement;
        const isOutsideClicked = !(
          this.appSidebar.el.nativeElement.isSameNode(target) ||
          this.appSidebar.el.nativeElement.contains(target) ||
          this.appTopbar.menuButton.nativeElement.isSameNode(target) ||
          this.appTopbar.menuButton.nativeElement.contains(target) ||
          target.classList.contains('collapsible-icon')
        );

        if (isOutsideClicked) {
          this.hideMenu();
        }
      });
    }

    if (!this.profileMenuOutsideClickListener) {
      this.profileMenuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
        const target = event.target as HTMLElement;
        const isOutsideClicked = !(
          this.appTopbar.menu.nativeElement.isSameNode(target) ||
          this.appTopbar.menu.nativeElement.contains(target) ||
          this.appTopbar.topbarMenuButton.nativeElement.isSameNode(target) ||
          this.appTopbar.topbarMenuButton.nativeElement.contains(target) ||
          target.classList.contains('collapsible-icon')
        );

        if (isOutsideClicked) {
          this.hideProfileMenu();
        }
      });
    }
  }
}
